import { useEffect } from 'react';

import { Card, Flex } from '@grupoboticario/flora-react';
import {
  InformationCircleIcon,
  CheckCircleFillIcon,
  HourglassIcon,
} from '@grupoboticario/flora-react-icons';

import { ErrorBoundaries, ProgressBar, ResponsiveFloraTypography } from 'components';
import { getFormattedPrice, getLongDateTime, pluralize } from 'helpers';
import { useIncentiveCampaign, useTracking } from 'hooks';
import { useLoyaltyIncentiveCampaign } from 'pages/IncentiveCampaign/hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './Campaign.styles';
import { getChallenges } from '../../helpers';

import type { AxiosResponse } from 'axios';

export const Campaign = () => {
  const { accumulated } = useIncentiveCampaign;
  const { campaignId } = useLoyaltyIncentiveCampaign();
  const {
    data,
    isError,
    error: accumulatedError,
    isSuccess,
    isLoading,
  } = accumulated.useQuery(campaignId);
  const { sendDataToGTM } = useTracking();

  useEffect(() => {
    if (!isLoading) {
      const error = accumulatedError as AxiosResponse;
      newRelicMonitoringService.addPageAction('callback', {
        event_name: 'callback_desafios_campanha_de_incentivos',
        page_title: 'Campanha de Incentivos',
        cd_interaction_detail: isSuccess ? 'sucesso' : `erro: ${error.status}`,
      });

      sendDataToGTM({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'callback_desafios_campanha_de_incentivos',
          cd_detail: isSuccess ? 'sucesso' : `erro: ${error.status}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isLoading) return <></>;

  if (isError)
    return (
      <ErrorBoundaries.ReloadPage
        title='Tivemos um problema para exibir o Compre e ganhe.'
        data-autotagging-flora-section={document}
      />
    );

  const levelChallenges = getChallenges(data.challenges);
  const currentChallenge = levelChallenges.current;
  const nextChallenge = levelChallenges.next;

  return (
    <Flex direction='column' gap='$8'>
      <Flex direction='column' gap='$6'>
        <ResponsiveFloraTypography as='h2' fontSize='title' fontWeight='bold'>
          Compre e ganhe prêmios
        </ResponsiveFloraTypography>

        <Flex direction='column' gap='$4'>
          <ResponsiveFloraTypography color='$nonInteractivePredominant'>
            Durante os ciclos 4, 5 e 6, todas as suas compras valem pontos. Mas atenção: o acúmulo
            só começa a valer a partir do ciclo que você realizou a sua inscrição e somente quando
            você alcançar o Desafio 1!
            <br />
            São {data?.challenges?.length} desafios que podem até triplicar seus pontos
            conquistados, e ao final do período, seus pontos serão liberados para você resgatar
            prêmios incríveis no catálogo do incentivo, não é demais?
          </ResponsiveFloraTypography>
        </Flex>
      </Flex>

      <Styles.Wrapper>
        {data.points !== null ? (
          <Styles.TotalCard>
            <Flex direction='column' gap='$4' align='center'>
              <ResponsiveFloraTypography as='h4' fontSize='subtitle' fontWeight='bold'>
                Acumulado no período
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography
                fontSize='subtitle'
                color='$nonInteractiveEmphasis'
                fontWeight='bold'
              >
                {data?.points} pontos
              </ResponsiveFloraTypography>
            </Flex>
          </Styles.TotalCard>
        ) : (
          <div></div>
        )}
        <Styles.ResellerProgress>
          <ResponsiveFloraTypography as='h4' fontSize='subtitle' fontWeight='bold'>
            Acompanhe seu progresso
          </ResponsiveFloraTypography>
          <Card>
            <Flex direction='column' gap='$4'>
              <Flex justify='space-between' gap='$4'>
                {currentChallenge != null ? (
                  <Flex direction='column' align='start'>
                    <ResponsiveFloraTypography
                      fontSize='bodySmallShort'
                      color='$nonInteractiveAuxiliar'
                    >
                      Nível atual
                    </ResponsiveFloraTypography>
                    <ResponsiveFloraTypography fontWeight='bold' fontSize='bodySmallShort'>
                      {currentChallenge.name}
                    </ResponsiveFloraTypography>
                  </Flex>
                ) : (
                  <div></div>
                )}

                {nextChallenge != null ? (
                  <Flex direction='column' align='end'>
                    <ResponsiveFloraTypography
                      fontSize='bodySmallShort'
                      color='$nonInteractiveAuxiliar'
                    >
                      Próximo nível
                    </ResponsiveFloraTypography>
                    <ResponsiveFloraTypography fontWeight='bold' fontSize='bodySmallShort'>
                      {nextChallenge.name}
                    </ResponsiveFloraTypography>
                  </Flex>
                ) : (
                  <div></div>
                )}
              </Flex>
              <Flex direction='column' gap='$2'>
                <ProgressBar
                  maxValue={nextChallenge?.amount ?? data.amount}
                  size='medium'
                  value={data.amount}
                />

                <Flex justify='start' align='center' gap='$1'>
                  <InformationCircleIcon size={14} />
                  <ResponsiveFloraTypography
                    fontSize='bodySmallShort'
                    color='$nonInteractiveAuxiliar'
                  >
                    Total acumulado no período
                  </ResponsiveFloraTypography>
                  <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
                    {getFormattedPrice(data.amount)}
                  </ResponsiveFloraTypography>
                </Flex>
              </Flex>

              <Styles.RemainingValue>
                {nextChallenge != null ? (
                  <Flex gap='$1' justify='center' wrap='wrap'>
                    <ResponsiveFloraTypography
                      fontSize='bodyLargeShort'
                      color='$nonInteractiveAuxiliar'
                    >
                      Faltam
                    </ResponsiveFloraTypography>
                    <ResponsiveFloraTypography
                      fontSize='bodyLargeShort'
                      color='$nonInteractiveEmphasis'
                      fontWeight='bold'
                    >
                      {getFormattedPrice(data.nextChallengeAmount)}
                    </ResponsiveFloraTypography>
                    <ResponsiveFloraTypography
                      fontSize='bodyLargeShort'
                      color='$nonInteractiveAuxiliar'
                    >
                      para o próximo desafio
                    </ResponsiveFloraTypography>
                  </Flex>
                ) : (
                  <ResponsiveFloraTypography
                    fontSize='bodyLargeShort'
                    color='$nonInteractiveAuxiliar'
                  >
                    Parabéns, você concluiu todos os desafios!🥳
                  </ResponsiveFloraTypography>
                )}
              </Styles.RemainingValue>
            </Flex>
          </Card>
          {data.lastUpdate != null && (
            <Styles.LastUpdate>
              <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                Última atualização
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                {getLongDateTime(data.lastUpdate)}
              </ResponsiveFloraTypography>
            </Styles.LastUpdate>
          )}
        </Styles.ResellerProgress>

        <Styles.ChallengesWrapper>
          <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold'>
            Desafios da campanha
          </ResponsiveFloraTypography>

          <Styles.Challenges size='medium'>
            {data?.challenges.map((challenge, index) => (
              <Styles.ChallengesCard
                data-complete={challenge.completed}
                key={`challenge-card-${index}`}
              >
                <Flex gap='$1'>
                  {challenge.completed ? (
                    <CheckCircleFillIcon color='$success-standard' size={24} />
                  ) : (
                    <HourglassIcon color='$nonInteractiveAuxiliar' size={24} />
                  )}

                  <Flex direction='column' gap='$4'>
                    <Flex direction='column'>
                      <ResponsiveFloraTypography
                        fontSize='exceptionsRestricted'
                        color='$nonInteractiveAuxiliar'
                      >
                        {challenge.name} - {challenge.completed ? 'CONCLUÍDO' : 'EM ANDAMENTO'}
                      </ResponsiveFloraTypography>

                      <ResponsiveFloraTypography
                        fontSize='bodySmallShort'
                        fontWeight='medium'
                        color='$nonInteractiveAuxiliar'
                      >
                        A partir de {getFormattedPrice(challenge.amount)}
                      </ResponsiveFloraTypography>
                    </Flex>

                    <ResponsiveFloraTypography fontWeight='bold' color='$nonInteractiveAuxiliar'>
                      R$ 1,00 ={' '}
                      {new Intl.NumberFormat('pt', { minimumFractionDigits: 1 }).format(
                        challenge.multiplier
                      )}{' '}
                      {pluralize(challenge.multiplier, 'ponto', `pontos`)}
                    </ResponsiveFloraTypography>
                  </Flex>
                </Flex>
              </Styles.ChallengesCard>
            ))}
          </Styles.Challenges>
        </Styles.ChallengesWrapper>
      </Styles.Wrapper>
    </Flex>
  );
};
